import { onBeforeUnmount, onMounted, ref } from 'vue'

export const useBlocksNavigation = (sections: string[], rootMargin: number) => {
  const activeSection = ref('')
  const isUserClicked = ref(false)
  const observers: IntersectionObserver[] = []
  const options = {
    rootMargin: `-${rootMargin}px`,
    root: document,
    treshhold: 0.5,
  }

  const scrollToBlock = (id: string) => {
    const element = document.getElementById(id)
    activeSection.value = id
    isUserClicked.value = true

    window.scrollTo({
      top: element ? element?.offsetTop : 0,
      behavior: 'smooth',
    })
  }

  const isInViewport = (element?: Element | null) => {
    if (!element) {
      return false
    }

    const rect = element.getBoundingClientRect()
    return (
      rect.bottom >= 0 && rect.top <= (window.innerHeight || document.documentElement.clientHeight)
    )
  }

  const changeNav = (entries: IntersectionObserverEntry[]) => {
    let updated = false // whether the menu was already updated during this intersection.

    entries.forEach((entry) => {
      if (updated) {
        return
      }

      let activeBlock

      if (!entry.isIntersecting) {
        // This element has just left the viewport. This means at least one of 2 possibilities:
        // 1. the next element has its top aligned with the top of the viewport.
        //    In this case the next element should be activated.
        // 2. the previous element is (at least partly) visible.
        //    In this case we need to keep looking at previous elements until
        //    we find the highest one that is still visible.

        if (isInViewport(entry.target.nextElementSibling)) {
          // possibility 1.
          activeBlock = entry.target.nextElementSibling
        } else if (isInViewport(entry.target.previousElementSibling)) {
          // possibility 2.
          activeBlock = entry.target.previousElementSibling
          while (isInViewport(activeBlock?.previousElementSibling)) {
            activeBlock = activeBlock?.previousElementSibling
          }
        }
      } else {
        // This element just entered the viewport. 2 possibilities:
        // 1. The top of the element moved in from the bottom.
        //    In this case nothing needs to happen.
        // 2. The bottom of the element moved in from the top.
        //    In this case the current element should also become active.

        if (!isInViewport(entry.target.nextElementSibling)) {
          // possibility 1.
          return
        } else if (!isInViewport(entry.target.previousElementSibling)) {
          // possibility 2.
          activeBlock = entry.target
        }
      }

      if (!activeBlock) {
        return
      }

      if (isUserClicked.value) {
        setTimeout(() => (isUserClicked.value = false), 500)
        return
      }

      activeSection.value = activeBlock.getAttribute('id')!
      updated = true
    })
  }

  onMounted(() => {
    const observer = new IntersectionObserver(changeNav, options)

    sections.forEach((id) => {
      const section = document.getElementById(id)
      if (section) {
        observer.observe(section)
        observers.push(observer)
      }
    })
  })

  onBeforeUnmount(() => observers.forEach((observer) => observer.disconnect()))

  return {
    scrollToBlock,
    activeSection,
  }
}
